import { Api } from '@/Api';
import { isEqual } from 'lodash';
import { defineStore } from 'pinia';
import { toRaw } from 'vue';

export enum CornerStyle {
  SLIGHTLY_ROUNDED = 'cta-slightly-rounded',
  ROUNDED = 'cta-rounded',
  SQUARE = 'cta-square',
}

export enum Appearance {
  HOVER = 'cta-button-hover',
  BELOW = 'cta-button-below',
}
export interface CallToActionItem {
  id?: string;
  templateId?: string;
  itemId?: string;
  link: string;
  text: string;
  createdAt?: string;
  updatedAt?: Date;
  appearance: Appearance;
  buttonColor: string;
  cornerStyle: CornerStyle;
  displayAction: boolean;
  textColor: string;
}

export interface CallToActionState {
  isLoaded: boolean;
  isSaving: boolean;
  cta?: CallToActionItem;
  ctaOriginal?: CallToActionItem;
  hoverableButtonVisible: boolean;
  showHoverableButton: boolean;
}

export const useCallToActionStore = defineStore('callToActionStore', {
  state: (): CallToActionState => ({
    isLoaded: false,
    isSaving: false,
    cta: {
      link: '',
      text: 'My Custom Button',
      appearance: Appearance.BELOW,
      cornerStyle: CornerStyle.SQUARE,
      displayAction: false,
      buttonColor: '#007AFF',
      textColor: '#FFFFFF',
    },
    showHoverableButton: false,
    ctaOriginal: undefined,
    hoverableButtonVisible: false,
  }),
  actions: {
    // @todo - fix it - we dot show any message according to show error  on request error
    async getCallToAction(ctaId: string) {
      try {
        if (this.isLoaded) {
          return Promise.resolve();
        }

        const response = await Api.item.callToActions.get(ctaId);

        this.isLoaded = true;

        this.cta = {
          id: ctaId,
          ...response.attributes,
        };
        return Promise.resolve(this.cta);
      } catch (error) {
        this.isLoaded = false;
        console.warn(error);
      }
    },
    async applyLastSettings() {
      const data = await Api.item.callToActions.last();
      if (!data || !data.id) {
        return Promise.reject();
      }
      this.$patch({ cta: { ...data.attributes } });

      return Promise.resolve(this.cta);
    },
    async save({ itemId, templateId }: { itemId?: string; templateId?: string }) {
      if (!this.cta) {
        return Promise.reject('Cta data not found');
      }

      if (!itemId && !templateId) {
        return Promise.reject('Missing required params');
      }

      this.isSaving = true;

      try {
        const response = await Api.item.callToActions.post({
          itemId,
          templateId,
          callToAction: {
            slug: this.cta.id,
            link: this.cta.link,
            text: this.cta.text,
            appearance: this.cta.appearance,
            buttonColor: this.cta.buttonColor,
            cornerStyle: this.cta.cornerStyle,
            displayAction: this.cta.displayAction,
            textColor: this.cta.textColor,
          },
        });

        this.cta = {
          id: this.cta.id,
          ...response.attributes,
        };

        this.isSaving = false;

        return Promise.resolve(this.cta);
      } catch (e: any) {
        console.log(e);
        this.isSaving = false;

        if (e.errors && e.errors.length) {
          return Promise.reject(e.errors[0].detail);
        }

        return Promise.reject('Cannot save call to action settings');
      }
    },
    ctaHoverableButtonVisible(value: boolean) {
      this.hoverableButtonVisible = value;
    },
    setCta(ctaData: CallToActionItem) {
      this.$patch({ cta: { ...ctaData } });
    },
    backupCta() {
      if (!this.cta) {
        return;
      }

      this.ctaOriginal = { ...this.cta };
    },
    restoreCta() {
      if (!this.ctaOriginal) {
        return;
      }

      this.cta = { ...this.ctaOriginal };
    },
    enableDisplayAction() {
      if (!this.cta) {
        return;
      }

      this.cta.displayAction = true;
    },
    setHoverableButtonVisibility(value: boolean) {
      this.showHoverableButton = value;
    },
  },
  getters: {
    isDirty: state => {
      if (!state.cta || !state.ctaOriginal) {
        return false;
      }

      return !isEqual(toRaw(state.cta), toRaw(state.ctaOriginal));
    },
    buttonClass(state) {
      if (state?.cta?.cornerStyle === CornerStyle.ROUNDED) {
        return 'rounded-full';
      }

      if (state?.cta?.cornerStyle === CornerStyle.SLIGHTLY_ROUNDED) {
        return 'rounded';
      }
    },
    isHoverable(state): boolean {
      return state?.cta?.appearance === Appearance.HOVER;
    },
    cornerStyleOptions: () => {
      return [
        {
          value: CornerStyle.SQUARE,
          label: 'Square',
        },
        {
          value: CornerStyle.SLIGHTLY_ROUNDED,
          label: 'Slightly Rounded',
        },
        {
          value: CornerStyle.ROUNDED,
          label: 'Rounded',
        },
      ];
    },
    appearanceOptions: () => {
      return [
        {
          value: Appearance.BELOW,
          label: 'Below content',
        },
        {
          value: Appearance.HOVER,
          label: 'Hover on content',
        },
      ];
    },
    appearanceOptionsVideo: () => {
      return [
        {
          value: Appearance.BELOW,
          label: 'Below content',
        },
        {
          value: Appearance.HOVER,
          label: 'When video ends',
        },
      ];
    },
    color: state => state?.cta?.textColor,
    background: state => state?.cta?.buttonColor,
    displayActionActive: state => state?.cta?.displayAction,
    buttonText: state => state?.cta?.text,
    outboundLink: state => {
      if (!state?.cta?.id) {
        return state?.cta?.link;
      }

      return Api.item.callToActions.link(state.cta.id);
    },
  },
});
